import React, { useEffect, useRef, useState } from 'react'
import { getDateOnly } from '../../utils/dateUtils'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import { Button, Checkbox, FormControlLabel, CircularProgress, Divider, Typography } from '@mui/material'
import Textarea from '@mui/joy/Textarea'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import Fab from '@mui/material/Fab'
import TextField from '@mui/material/TextField'
import FormControlJoy from '@mui/joy/FormControl'
import RadioJoy from '@mui/joy/Radio'
import RadioGroupJoy from '@mui/joy/RadioGroup'
import toast from 'react-hot-toast'
import { BeatLoader } from 'react-spinners'
import axios from '../../api/axios'
import { useSelector } from 'react-redux'
import io from 'socket.io-client'
import DOMPurify from 'dompurify'

const socket = io(process.env.REACT_APP_BASE_URL)
// Note: nginx client_max_body_size is 25 MB — keep this limit below that so the site rejects before the web server does
const MAX_FILE_SIZE = 20971520 // 20 MB
const MAX_FILES = 3

const PRICING_SERVICES = [
   { key: 'EGR_OFF', label: 'EGR OFF' },
   { key: 'DPF_OFF', label: 'DPF OFF' },
   { key: 'DTC_OFF', label: 'DTC OFF' },
   { key: 'ADBLUE_OFF', label: 'AdBlue OFF' },
   { key: 'OPF_OFF', label: 'GPF/OPF OFF' },
   { key: 'NOX_OFF', label: 'NOX OFF' },
   { key: 'SWIRL_FLAPS', label: 'Swirl flaps' },
   { key: 'POP_BANG', label: 'Pop & Bang' },
   { key: 'HARDCUT', label: 'Popcorn / Hardcut' },
   { key: 'E85_FLEX', label: 'E85 / Flex Fuel' },
   { key: 'DECAT', label: 'DECAT' },
   { key: 'VMAX_OFF', label: 'VMAX OFF' },
   { key: 'ORIGINAL_FILE', label: 'Original file' },
]


const VEHICLE_BRANDS = [
   'Abarth',
   'Acura',
   'Aiways',
   'Aixam',
   'Alfa-Romeo',
   'Alpine',
   'Aprilia',
   'Aston-Martin',
   'Audi',
   'BAIC',
   'Bentley',
   'BMW',
   'BRP',
   'Buick',
   'Bugatti',
   'BYD',
   'Cadillac',
   'Can-Am',
   'Case IH',
   'Caterham',
   'Changan',
   'Chery',
   'Chevrolet',
   'Chrysler',
   'Citroen',
   'Claas',
   'Cupra',
   'Dacia',
   'DAF',
   'Daihatsu',
   'Deutz-Fahr',
   'Dodge',
   'DS',
   'Ducati',
   'Ferrari',
   'Fendt',
   'Fiat',
   'Fisker',
   'Ford',
   'GasGas',
   'Geely',
   'Genesis',
   'GMC',
   'Great Wall',
   'Harley-Davidson',
   'Honda',
   'Hongqi',
   'Hummer',
   'Husqvarna',
   'Hyosung',
   'Hyundai',
   'Ineos',
   'Indian',
   'Infiniti',
   'Isuzu',
   'Iveco',
   'Jaguar',
   'Jeep',
   'John Deere',
   'Kawasaki',
   'KGM',
   'Kia',
   'KTM',
   'Lamborghini',
   'Lancia',
   'Land Rover',
   'Leapmotor',
   'Lexus',
   'Lincoln',
   'Lotus',
   'Lucid',
   'Lynk & Co',
   'MAN',
   'Maserati',
   'Massey Ferguson',
   'Maxus',
   'Mazda',
   'McLaren',
   'Mercedes',
   'MG',
   'Mini',
   'Mitsubishi',
   'Mitsuoka',
   'Moto Guzzi',
   'MV Agusta',
   'New Holland',
   'Nio',
   'Nissan',
   'Norton',
   'Opel',
   'Pagani',
   'Peugeot',
   'Polaris',
   'Polestar',
   'Porsche',
   'Ram',
   'Renault',
   'Renault Trucks',
   'Rezvani',
   'Rivian',
   'Rolls-Royce',
   'Royal Enfield',
   'Saab',
   'Scania',
   'Scion',
   'Seat',
   'Seres',
   'Skoda',
   'Smart',
   'SsangYong',
   'Subaru',
   'Suzuki',
   'Tata',
   'Tesla',
   'Toyota',
   'Triumph',
   'Valtra',
   'Vauxhall',
   'Volkswagen',
   'Volvo',
   'XPeng',
   'Yamaha',
   'Zeekr',
]

const getTuningTypeOptions = (vehicleType) => {
   if (vehicleType === 'Gearbox') {
      return [
         { value: 'Stage 1', label: 'Stage 1' },
         { value: 'Stage 2', label: 'Stage 2' },
      ]
   }

   if (['Trucks', 'Tractor', 'Bus'].includes(vehicleType)) {
      return [
         { value: 'Stage 1', label: 'Stage 1' },
         { value: 'Original', label: 'Original' },
      ]
   }

   if (['Motorcycles', 'Powersports', 'Boats'].includes(vehicleType)) {
      return [
         { value: 'Stage 1', label: 'Stage 1' },
      ]
   }

   return [
      { value: 'Stage 1', label: 'Stage 1' },
      { value: 'Stage 2', label: 'Stage 2' },
      { value: 'Stage 3', label: 'Stage 3' },
      { value: 'A-traktor', label: 'A-traktor' },
      { value: 'Original', label: 'Original' },
   ]
}

const getVisiblePricingServices = (vehicleType) => {
   if (vehicleType === 'Gearbox') {
      return []
   }

   if (['Trucks', 'Tractor', 'Bus'].includes(vehicleType)) {
      const allowed = new Set([
         'EGR_OFF',
         'DPF_OFF',
         'ADBLUE_OFF',
      ])

      return PRICING_SERVICES.filter(
         option => allowed.has(option.key)
      )
   }

   return PRICING_SERVICES
}

const getLegacyExtrasValue = (services) => {
   if (!services.length) return ''

   if (services.length === 1) {
      const option = PRICING_SERVICES.find(
         item => item.key === services[0]
      )
      return option?.label || services[0]
   }

   return 'MULTIPLE OPTIONS'
}

export default function Upload() {
   const navigate = useNavigate()
   const [account,setAccount] = useState(null);
   const [supportID, setSupportID] = useState('')
   const [userName, setUserName] = useState('')
   const [creditAmount, setCreditAmount] = useState(0)

   const [vehicleType, setVehicleType] = useState('')
   const [vehicleBrand, setVehicleBrand] = useState('')
   const [vehicleSeries, setVehicleSeries] = useState('')
   const [buildYear, setBuildYear] = useState()
   const [buildYearValue, setBuildYearValue] = useState('')
   const [uploadBtnFlag, DisableUploadButton] = useState(true)

   const [HP, setHP] = useState('')
   const [KW, setKW] = useState('')
   const [transmission, setTransmission] = useState('')
   const [VINnumber, setVINnumber] = useState('')

   const [registrationNumber, setRegistrationNumber] = useState('')
   const [vehicleLookupLoading, setVehicleLookupLoading] = useState(false)
   const [vehicleLookupMessage, setVehicleLookupMessage] = useState('')

   const [tuningType, setTuningType] = useState('')
   const [extras, setExtras] = useState('')
   const [message, setMessage] = useState('')
   const [term, setTerm] = useState(false)

   const [userId, setUserID] = useState('')
   const [fileData, setFileData] = useState([])
   const inputElement = useRef('fileInput')
   const [dragOver, setDragOver] = useState(false)

   const [policy, setPolicy] = useState('')
   const [fileOpen, setFileOpen] = useState(false)
   const [termViewFlag, setTermViewFlag] = useState(false)
   const [isLoading, setIsLoading] = useState(false)
   const [enoughCredits, setEnoughCredits] = useState(false)

   const [requestedServices, setRequestedServices] = useState([])
   const [adBlueClass, setAdBlueClass] = useState('')
   const [pricingQuote, setPricingQuote] = useState(null)
   const [pricingLoading, setPricingLoading] = useState(false)


   useEffect(()=>{
      const user = localStorage.getItem('user');
      if(user){
         let parse = JSON.parse(user);
         setAccount(parse);
      }
   },[]);

   useEffect(() => {
      const allowedTuningTypes = getTuningTypeOptions(vehicleType)

      if (
         tuningType &&
         !allowedTuningTypes.some(
            option => option.value === tuningType
         )
      ) {
         setTuningType('')
      }

      const allowedServices = new Set(
         getVisiblePricingServices(vehicleType).map(
            option => option.key
         )
      )

      setRequestedServices(current => {
         const next = current.filter(
            key => allowedServices.has(key)
         )

         if (
            next.length === current.length &&
            next.every((key, index) => key === current[index])
         ) {
            return current
         }

         return next
      })

      if (
         ['Trucks', 'Tractor', 'Bus', 'Gearbox'].includes(vehicleType)
      ) {
         setAdBlueClass('')
      }
   }, [vehicleType, tuningType])


   useEffect(() => {
      setExtras(getLegacyExtrasValue(requestedServices))

      if (!requestedServices.includes('ADBLUE_OFF')) {
         setAdBlueClass('')
      }
   }, [requestedServices])

   useEffect(() => {
      if (!tuningType || !vehicleType) {
         setPricingQuote(null)
         setPricingLoading(false)
         return
      }

      let cancelled = false

      const timer = setTimeout(async () => {
         setPricingLoading(true)

         try {
            const result = await axios.post('pricing/quote', {
               tuningType,
               vehicleType,
               services: requestedServices,
               adBlueClass,
            })

            if (!cancelled) {
               setPricingQuote(result.data)
            }
         } catch (error) {
            if (!cancelled) {
               setPricingQuote({
                  valid: true,
                  autoPriced: false,
                  manualReview: true,
                  reason: 'Automatic price could not be calculated.',
               })
            }
         } finally {
            if (!cancelled) {
               setPricingLoading(false)
            }
         }
      }, 250)

      return () => {
         cancelled = true
         clearTimeout(timer)
      }
   }, [tuningType, vehicleType, requestedServices, adBlueClass])

   const toggleRequestedService = (serviceKey) => {
      setRequestedServices((current) => {
         const adBlueIncludesNox =
            !['Trucks', 'Tractor', 'Bus', 'Gearbox'].includes(vehicleType)

         /*
          * AdBlue OFF always includes NOX OFF.
          */
         if (
            serviceKey === 'ADBLUE_OFF' &&
            adBlueIncludesNox
         ) {
            if (current.includes('ADBLUE_OFF')) {
               return current.filter(
                  item =>
                     item !== 'ADBLUE_OFF' &&
                     item !== 'NOX_OFF'
               )
            }

            return [
               ...current,
               'ADBLUE_OFF',
               ...(current.includes('NOX_OFF')
                  ? []
                  : ['NOX_OFF']),
            ]
         }

         /*
          * NOX cannot be removed while AdBlue is selected.
          */
         if (
            serviceKey === 'NOX_OFF' &&
            adBlueIncludesNox &&
            current.includes('ADBLUE_OFF')
         ) {
            return current
         }

         if (current.includes(serviceKey)) {
            return current.filter(item => item !== serviceKey)
         }

         if (
            serviceKey === 'DPF_OFF' &&
            current.includes('OPF_OFF')
         ) {
            toast.error('DPF OFF and GPF/OPF OFF cannot be selected together')
            return current
         }

         if (
            serviceKey === 'OPF_OFF' &&
            current.includes('DPF_OFF')
         ) {
            toast.error('GPF/OPF OFF and DPF OFF cannot be selected together')
            return current
         }

         return [...current, serviceKey]
      })
   }


   const lookupSwedishVehicle = async () => {
      const reg = String(registrationNumber || '')
         .trim()
         .toUpperCase()
         .replace(/[\s-]/g, '')

      if (!reg) return

      setVehicleLookupLoading(true)
      setVehicleLookupMessage('')

      try {
         const result = await axios.get(
            `toolbox/vehicle/${encodeURIComponent(reg)}`
         )

         const vehicle = result?.data?.data

         if (!result?.data?.status || !vehicle) {
            throw new Error(
               result?.data?.message || 'Vehicle could not be found.'
            )
         }

         const normalize = (value) =>
            String(value || '')
               .normalize('NFD')
               .replace(/[\u0300-\u036f]/g, '')
               .replace(/[^A-Z0-9]+/gi, ' ')
               .trim()
               .toUpperCase()

         const typeText = normalize(
            vehicle.vehicle_type || vehicle.vehicle_class
         )

         const categoryText = normalize(
            vehicle.vehicle_category
         )

         const grossWeightKg = Number(
            String(vehicle.gross_weight || '')
               .replace(/[^0-9]/g, '')
         )

         let mappedType = ''

         if (typeText.includes('PERSONBIL')) {
            mappedType = 'Cars'
         }
         else if (typeText.includes('MOTORCYKEL')) {
            mappedType = 'Motorcycles'
         }
         else if (
            categoryText === 'N1' ||
            (
               typeText.includes('LASTBIL') &&
               Number.isFinite(grossWeightKg) &&
               grossWeightKg > 0 &&
               grossWeightKg <= 3500
            )
         ) {
            mappedType = 'Cars'
         }
         else if (typeText.includes('LASTBIL')) {
            mappedType = 'Trucks'
         }
         else if (typeText.includes('TRAKTOR')) {
            mappedType = 'Tractor'
         }
         else if (typeText.includes('BUSS')) {
            mappedType = 'Bus'
         }

         const makeText = normalize(vehicle.make)

         /*
          * Prefer the most specific manufacturer match.
          * Example: RENAULT TRUCKS must match Renault Trucks,
          * not the shorter Renault prefix.
          */
         let mappedBrand =
            VEHICLE_BRANDS
               .filter((brand) => {
                  const candidate = normalize(brand)
                  return (
                     makeText === candidate ||
                     makeText.startsWith(`${candidate} `)
                  )
               })
               .sort(
                  (a, b) =>
                     normalize(b).length -
                     normalize(a).length
               )[0] || ''

         if (!mappedBrand && makeText.startsWith('MERCEDES BENZ')) {
            mappedBrand = 'Mercedes'
         }

         /*
          * Fallback for manufacturers not yet present in
          * VEHICLE_BRANDS. Keep the registration lookup usable
          * instead of leaving Vehicle Brand blank.
          */
         if (!mappedBrand && vehicle.make) {
            mappedBrand = String(vehicle.make).trim()
         }

         let series = String(vehicle.model || '').trim()

         if (series && mappedBrand) {
            const normalizedSeries = normalize(series)
            const normalizedBrand = normalize(mappedBrand)

            if (normalizedSeries.startsWith(`${normalizedBrand} `)) {
               const wordsToRemove = normalizedBrand.split(' ').length
               series = series
                  .split(/\s+/)
                  .slice(wordsToRemove)
                  .join(' ')
            }
         }

         const gearbox = normalize(vehicle.gearbox)
         let mappedTransmission = ''

         if (gearbox.includes('MANUELL')) {
            mappedTransmission = 'Manual'
         } else if (
            gearbox.includes('AUTOMAT') ||
            gearbox.includes('AUTOMATIC')
         ) {
            mappedTransmission = 'Automatic'
         }

         if (mappedType) setVehicleType(mappedType)
         if (mappedBrand) setVehicleBrand(mappedBrand)
         if (series) setVehicleSeries(series)

         const year = Number(vehicle.vehicle_year)
         if (Number.isFinite(year) && year > 1900) {
            setBuildYearValue(year)
            setBuildYear(year)
         }

         const powerKW = Number(vehicle.power_kw)
         if (Number.isFinite(powerKW) && powerKW > 0) {
            setHP(Math.round(powerKW / 0.73549875))
         }

         if (mappedTransmission) {
            setTransmission(mappedTransmission)
         }

         if (vehicle.vin) {
            setVINnumber(vehicle.vin)
         }

         setRegistrationNumber(
            vehicle.registration_number || reg
         )

         const summary = [
            vehicle.registration_number || reg,
            mappedBrand,
            series,
            vehicle.vehicle_year,
         ]
            .filter(Boolean)
            .join(' · ')

         setVehicleLookupMessage(
            `Vehicle data loaded${summary ? `: ${summary}` : ''}`
         )

         toast.success('Vehicle data loaded')
      } catch (error) {
         if (process.env.REACT_APP_MODE) console.log(error)

         setVehicleLookupMessage('')
         toast.error(
            'Vehicle could not be found. You can continue manually.'
         )
      } finally {
         setVehicleLookupLoading(false)
      }
   }
   const getContents = async () => {
      try {
         await axios
            .post(`getContents`)
            .then((result) => {
               setPolicy(result.data.privacy)
            })
      } catch (error) {
         if (process.env.REACT_APP_MODE) console.log(error)
      }
   }

   const getSumCredit = async (id) => {
      try {
         await axios
            .post(`getSumCredit`, { id })
            .then((result) => {
               if (result.data.status) {
                  setCreditAmount( result.data.data )
                  setEnoughCredits( result.data.data >= 45 )

               } else {
                  toast.error(result.data.data)
               }
            })
      } catch (error) {
         if (process.env.REACT_APP_MODE) console.log(error)
      }
   }

   const handleFileload = () => {
      inputElement.current.click()
   }

   const BLOCKED_TYPES = ['exe','bat','scr','vbs','cmd','py','jar','pl','csh','sh']

   const validateAndAddFiles = (newFiles) => {
      const valid = []
      for (const fileobj of newFiles) {
         const ext = fileobj.name.split('.').pop().toLowerCase()
         if (BLOCKED_TYPES.includes(ext)) {
            toast.error(`Unsupported file type: ${fileobj.name}`)
         } else if (fileobj.size > MAX_FILE_SIZE) {
            toast.error(`${fileobj.name} exceeds 20 MB limit`)
         } else {
            valid.push(fileobj)
         }
      }
      if (valid.length > 0) setFileData(prev => {
         const combined = [...prev, ...valid]
         if (combined.length > MAX_FILES) {
            toast.error(`Max ${MAX_FILES} files allowed`)
            return prev
         }
         return combined
      })
   }

   const getFile = (e) => {
      validateAndAddFiles(Array.from(e.target.files))
      e.target.value = ''
   }

   const handleDragOver = (e) => {
      e.preventDefault()
      setDragOver(true)
   }

   const handleDragLeave = () => setDragOver(false)

   const handleDrop = (e) => {
      e.preventDefault()
      setDragOver(false)
      validateAndAddFiles(Array.from(e.dataTransfer.files))
   }

   const upload = async () => {
      if (fileData.length === 0) {
         toast.error('No file selected')
         return
      }
      if (!vehicleType) {
         toast.error('Select a Vehicle type')
         return
      }
      if (!vehicleBrand) {
         toast.error('Select a Vehicle brand')
         return
      }
      if (!vehicleSeries) {
         toast.error('Select a Vehicle series')
         return
      }
      if (!buildYearValue) {
         toast.error('Select a Build Year')
         return
      }
      if (!HP || !KW) {
         toast.error('Select an engine power (HP)')
         return
      }
      if (!transmission) {
         toast.error('Select a Transmission type')
         return
      }
      if (!VINnumber) {
         toast.error('Select a VIN number')
         return
      }
      if (!tuningType) {
         toast.error('Select a Tuning Type')
         return
      }

      if (
         requestedServices.includes('ADBLUE_OFF') &&
         !['Trucks', 'Tractor', 'Bus', 'Gearbox'].includes(vehicleType) &&
         (
            !adBlueClass ||
            String(adBlueClass).trim().toUpperCase() === 'UNKNOWN'
         )
      ) {
         toast.error('Please select the AdBlue ECU type')
         return
      }

      if (
         String(tuningType).trim().toLowerCase() === 'original' &&
         requestedServices.length === 0
      ) {
         toast.error('Select at least one service for Original')
         return
      }

      if (!term) {
         toast.error('Please accept the terms')
         return
      }
      let params = new FormData()
      const data = {
         orderId: '',
         userId,
         email: account?.email,
         client: userName,
         fileName: fileData.map(f => f.name),
         fileSize: fileData.map(f => f.size),
         fileType: fileData.map(f => f.type),
         fileRename: fileData.map(f => f.lastModified),
         vehicleType,
         vehicleBrand,
         vehicleSeries,
         buildYear,
         HP,
         KW,
         transmission,
         registrationNumber: String(registrationNumber || '')
            .trim()
            .toUpperCase()
            .replace(/[\s-]/g, ''),
         VINnumber,
         tuningType,
         extras,
         requestedServices,
         adBlueClass,
         message,
         note: '',
         status: 'requested',
         createdAt: getDateOnly(),
         readStatus: false,
      }
      fileData.forEach(f => params.append('file', f))
      params.append('data', JSON.stringify(data))
      if (!isLoading) {
         setIsLoading(true)
         try {
            await axios
               .post(`uploadFile`, params)
               .then((result) => {
                  if (result.data.status) {
                     socket.emit('request', { to: supportID, name: userName })
                     toast.success(result.data.data)
                     setUserName('')
                     setVehicleType('')
                     setVehicleBrand('')
                     setVehicleSeries('')
                     setBuildYear(new Date())
                     setHP('')
                     setKW('')
                     setTransmission('')
                     setVINnumber('')

                     setRegistrationNumber('')
                     setVehicleLookupMessage('')
                     setTuningType('')
                     setExtras('')
                     setRequestedServices([])
                     setAdBlueClass('')
                     setPricingQuote(null)
                     setMessage('')
                     setTerm('')
                     setFileData([])
                     setIsLoading(false)
                     navigate('/dashboard')
                  } else {
                     toast.error(result.data.data)
                  }
               })
         } catch (error) {
            if (process.env.REACT_APP_MODE) console.log(error)
         }
      } else {
         toast.error('Loading...')
      }
   }

   const getSupportID = async () => {
      try {
         await axios
            .post(`getSupportID`)
            .then(async (result) => {
               if (result.data.status) await setSupportID(result.data.data)
            })
      } catch (error) {
         if (process.env.REACT_APP_MODE) console.log(error)
      }
   }

   const deleteFile = (index) => {
      setFileData(prev => prev.filter((_, i) => i !== index))
   }

   useEffect(() => {
      const now = new Date()
      const MaxYear = now.getFullYear()
      if (!buildYearValue || buildYearValue <= 0) {
         setBuildYearValue('')
      } else if( buildYearValue > MaxYear) {
         setBuildYearValue(MaxYear)
      /*
      } else if( buildYearValue < 1900) {
         setBuildYearValue(1900)
      */
      } else {
         setBuildYear(new Date(buildYearValue, 0))
      }
   }, [buildYearValue])

   /*
   useEffect(() => {
      console.log("Build date is " + buildYear)
   }, [buildYear])
   */

   useEffect(() => {
      console.log("Term: " + term)
      DisableUploadButton(!term || !enoughCredits)
   }, [term, enoughCredits])

   useEffect(() => {
      if (!HP || HP === 0) {
         setHP('')
         setKW('')
      } else {
         setKW( (HP * 0.73549875).toFixed(1) )
      }
   }, [HP, KW])

   useEffect(() => {
      setFileOpen(fileData.length > 0)
   }, [fileData])

   useEffect(() => {
      getSupportID()
      if (account) {
         setUserName(account?.name)
         setUserID(account?._id)
         getSumCredit(account?._id)
      }
   }, [account])

   useEffect(() => {
      getContents()
   }, [])



   return (
      <Box
         sx={{
            flexGrow: 1,
            p: 3,
            bgcolor: '#F3F6F9',
            overflowY: 'overlay',
         }}
      >
         {termViewFlag ? (
            <Box
               sx={{
                  flexGrow: 1,
                  p: 3,
                  bgcolor: '#F3F6F9',
                  overflowY: 'overlay',
               }}
            >
               <Box
                  sx={{
                     mt: '80px',
                     display: 'flex',
                     justifyContent: 'space-between',
                  }}
               >
                  <h2 style={{ color: '#163A5F', margin: '0px' }}>
                     Privacy Policy
                  </h2>
                  <Box>
                     <Fab
                        color="primary"
                        aria-label="add"
                        onClick={() => setTermViewFlag(false)}
                     >
                        <ArrowBackIcon />
                     </Fab>
                  </Box>
               </Box>
               <Box
                  sx={{
                     mt: '10px',
                     borderTop: '5px solid #163A5F',
                     bgcolor: 'white',
                     borderBottomRightRadius: '5px',
                     borderBottomLeftRadius: '5px',
                     overflow: 'auto',
                     p: '10px',
                     height: '60vh',
                  }}
                  id='description'
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(policy) }}
               />
            </Box>
         ) : (
            <>
               <Box sx={{ mt: '80px' }}>
                  <h3 style={{ color: '#163A5F', margin: '0px' }}>
                     Upload File
                  </h3>
               </Box>
               <Box
                  sx={{
                     mt: '10px',
                     borderTop: '5px solid #163A5F',
                     bgcolor: 'white',
                     borderBottomRightRadius: '5px',
                     borderBottomLeftRadius: '5px',
                     p: '10px',
                  }}
               >
                  <Grid
                     container
                     spacing={{ xs: 2, md: 3 }}
                     columns={{ xs: 4, sm: 6, md: 12 }}
                  >
                     <Grid item xs={12} sm={6} md={7}>
                        <Box
                           onDrop={handleDrop}
                           onDragOver={handleDragOver}
                           onDragLeave={handleDragLeave}
                           sx={{
                              display: 'flex',
                              justifyContent: 'flex-start',
                              border: dragOver ? '2px solid #163A5F' : '1px dotted',
                              borderRadius: '20px',
                              flexDirection: 'column',
                              p: '30px',
                              gap: '10px',
                              alignItems: 'center',
                              bgcolor: dragOver ? 'rgba(25,118,210,0.05)' : 'transparent',
                              transition: 'all 0.15s ease',
                           }}
                        >
                           <Box>
                              <CloudUploadIcon
                                 sx={{ color: '#163A5F', fontSize: '70px' }}
                              />
                           </Box>
                           <Box>Drag and drop your files here</Box>
                           <Box>or</Box>
                           <Box>
                              <input
                                 ref={inputElement}
                                 type="file"
                                 multiple
                                 style={{ display: 'none' }}
                                 onChange={(e) => getFile(e)}
                              />
                              <Button
                                 variant="outlined"
                                 sx={{
                                    border: '1px solid #163A5F',
                                    borderRadius: '12px',
                                    color: '#163A5F',
                                 }}
                                 onClick={handleFileload}
                              >
                                 Browse Files
                              </Button>
                           </Box>
                        </Box>
                     </Grid>
                     <Grid item xs={12} sm={6} md={5}>
                        {fileOpen ? (
                           <Box mt={4} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {fileData.map((f, i) => (
                                 <Box key={i} className="select-file">
                                    <Box className="file-group-box">
                                       <Box className="upload-img-box">
                                          <UploadFileIcon className="upload-img-icon" />
                                       </Box>
                                       <Box className="file-info">
                                          <Box>
                                             <Box className="filename-string">
                                                {f.name}
                                             </Box>
                                             <Box>
                                                {(f.size / 1000000).toFixed(4)}{' '}MB
                                             </Box>
                                          </Box>
                                       </Box>
                                    </Box>
                                    <Box className="close-img-box">
                                       <HighlightOffIcon
                                          className="close-img-icon"
                                          onClick={() => deleteFile(i)}
                                       />
                                    </Box>
                                 </Box>
                              ))}
                           </Box>
                        ) : (
                           <></>
                        )}
                     </Grid>
                     <Grid item xs={12} sm={12} md={12}>
                        { (creditAmount >= 100) ||
                          <Box
                            color="#B80F0A"
                            height="40px"
                            width="600px"
                            fontSize="20px"
                          >Your credits are running low. Consider buying more!
                        </Box>}
                     </Grid>
                     <Grid item xs={12}>
                        <Box
                           sx={{
                              mb: 1,
                              p: { xs: 1.5, sm: 2 },
                              border: '1px solid #D0D5DD',
                              borderRadius: 2,
                              bgcolor: '#F8FAFC',
                           }}
                        >
                           <Box
                              sx={{
                                 display: 'grid',
                                 gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'minmax(0, 1fr) auto',
                                 },
                                 gap: 1.5,
                                 alignItems: 'center',
                              }}
                           >
                              <TextField
                                 fullWidth
                                 size="small"
                                 label="Swedish registration number (optional)"
                                 placeholder="e.g. FXD36D"
                                 value={registrationNumber}
                                 onChange={(e) => {
                                    setRegistrationNumber(
                                       e.target.value.toUpperCase()
                                    )
                                    setVehicleLookupMessage('')
                                 }}
                                 onKeyDown={(e) => {
                                    if (
                                       e.key === 'Enter' &&
                                       !vehicleLookupLoading
                                    ) {
                                       e.preventDefault()
                                       lookupSwedishVehicle()
                                    }
                                 }}
                                 inputProps={{ maxLength: 10 }}
                              />

                              <Button
                                 variant="contained"
                                 disabled={
                                    !registrationNumber.trim() ||
                                    vehicleLookupLoading
                                 }
                                 onClick={lookupSwedishVehicle}
                                 sx={{
                                    minWidth: { sm: 145 },
                                    height: 40,
                                    bgcolor: '#163A5F',
                                    '&:hover': { bgcolor: '#0D2740' },
                                 }}
                              >
                                 {vehicleLookupLoading
                                    ? 'Fetching...'
                                    : 'Fetch vehicle'}
                              </Button>
                           </Box>

                           <Box
                              sx={{
                                 mt: 0.8,
                                 color: '#667085',
                                 fontSize: '12px',
                              }}
                           >
                              Optional. If no Swedish registration number is
                              available, continue by entering the vehicle data
                              manually.
                           </Box>

                           {vehicleLookupMessage && (
                              <Box
                                 sx={{
                                    mt: 1,
                                    color: '#067647',
                                    fontSize: '13px',
                                    fontWeight: 700,
                                 }}
                              >
                                 ✓ {vehicleLookupMessage}
                              </Box>
                           )}
                        </Box>
                     </Grid>
                     <Grid item xs={12} sm={6} md={6}>
                        Vehicle type
                        <FormControl fullWidth>
                           <Select
                              size="small"
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              value={vehicleType}
                              onChange={(e) => setVehicleType(e.target.value)}
                           >
                              <MenuItem value="Cars">Cars</MenuItem>
                              <MenuItem value="Motorcycles">Motorcycles</MenuItem>
                              <MenuItem value="Trucks">Trucks</MenuItem>
                              <MenuItem value="Tractor">Tractor</MenuItem>
                              <MenuItem value="Powersports">Powersports</MenuItem>
                              <MenuItem value="Boats">Boats</MenuItem>
                              <MenuItem value="Bus">Bus</MenuItem>
                              <MenuItem value="Gearbox">Gearbox</MenuItem>
                           </Select>
                        </FormControl>
                     </Grid>
                     <Grid item xs={12} sm={6} md={6}>
                        Vehicle Brand
                        <FormControl fullWidth>
                           <Select
                              size="small"
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              value={vehicleBrand}
                              onChange={(e) => setVehicleBrand(e.target.value)}
                           >
                              {vehicleBrand &&
                                 !VEHICLE_BRANDS.includes(vehicleBrand) && (
                                    <MenuItem
                                       value={vehicleBrand}
                                    >
                                       {vehicleBrand}
                                    </MenuItem>
                                 )}

                              {VEHICLE_BRANDS.map((brand) => (
                                 <MenuItem
                                    key={brand}
                                    value={brand}
                                 >
                                    {brand}
                                 </MenuItem>
                              ))}
                           </Select>
                        </FormControl>
                     </Grid>
                     <Grid item xs={12} sm={6} md={6}>
                        Vehicle Series
                        <FormControl fullWidth>
                           <TextField
                              size="small"
                              value={vehicleSeries}
                              onChange={(e) => setVehicleSeries(e.target.value)}
                           />
                        </FormControl>
                     </Grid>
                     <Grid item xs={12} sm={6} md={6}>
                        Build Year
                        <FormControl fullWidth>
                           <TextField
                              size="small"
                              value={buildYearValue}
                              type="number"
                              onChange={(e) => setBuildYearValue(Number(e.target.value))}
                           />
                        </FormControl>
                     </Grid>

                     <Grid item xs={12} sm={6} md={6}>
                        Power (HP)
                        <FormControl fullWidth>
                           <TextField
                              size="small"
                              value={HP}
                              type="number"
                              onChange={(e) => setHP(Number(e.target.value))}
                           />
                        </FormControl>
                     </Grid>
                     <Grid item xs={12} sm={6} md={6}>
                        Power (kW)
                        <FormControl fullWidth>
                           <TextField
                              size="small"
                              value={KW}
                              readOnly
                           />
                        </FormControl>
                     </Grid>
                     <Grid item xs={12} sm={6} md={6}>
                        Transmission Type
                        <FormControl fullWidth>
                           <Select
                              size="small"
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              value={transmission}
                              onChange={(e) => setTransmission(e.target.value)}
                           >
                              <MenuItem value="Manual">Manual</MenuItem>
                              <MenuItem value="Automatic">Automatic</MenuItem>
                           </Select>
                        </FormControl>
                     </Grid>
                     <Grid item xs={12} sm={6} md={6}>
                        VIN Number#
                        <FormControl fullWidth>
                           <TextField
                              size="small"
                              value={VINnumber}
                              onChange={(e) => setVINnumber(e.target.value)}
                           />
                        </FormControl>
                     </Grid>

                     <Grid item xs={12} sm={6} md={6}>
                        Tuning Type
                        <FormControl fullWidth>
                           <Select
                              size="small"
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              value={tuningType}
                              onChange={(e) => setTuningType(e.target.value)}
                           >
                              {getTuningTypeOptions(vehicleType).map(option => (
                                 <MenuItem
                                    key={option.value}
                                    value={option.value}
                                 >
                                    {option.label}
                                 </MenuItem>
                              ))}
                           </Select>
                        </FormControl>
                     </Grid>
                     <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        sx={{
                           display: vehicleType === 'Gearbox'
                              ? 'none'
                              : 'block',
                        }}
                     >
                        Services
                        <Box
                           sx={{
                              mt: 0.5,
                              p: 1.5,
                              border: '1px solid #dfe3e8',
                              borderRadius: 1,
                              display: 'grid',
                              gridTemplateColumns: {
                                 xs: '1fr',
                                 sm: '1fr 1fr',
                              },
                              columnGap: 2,
                           }}
                        >
                           {getVisiblePricingServices(vehicleType).map((option) => {
                              const checked =
                                 requestedServices.includes(option.key)

                              const disabled =
                                 (option.key === 'DPF_OFF' &&
                                    requestedServices.includes('OPF_OFF')) ||
                                 (option.key === 'OPF_OFF' &&
                                    requestedServices.includes('DPF_OFF')) ||
                                 (option.key === 'NOX_OFF' &&
                                    requestedServices.includes('ADBLUE_OFF'))

                              return (
                                 <FormControlLabel
                                    key={option.key}
                                    control={
                                       <Checkbox
                                          size="small"
                                          checked={checked}
                                          disabled={disabled}
                                          onChange={() =>
                                             toggleRequestedService(option.key)
                                          }
                                       />
                                    }
                                    label={
                                       option.key === 'NOX_OFF' &&
                                       requestedServices.includes('ADBLUE_OFF')
                                          ? 'NOX OFF (included)'
                                          : option.label
                                    }
                                 />
                              )
                           })}
                        </Box>
                     </Grid>

                     {requestedServices.includes('ADBLUE_OFF') &&
                        !['Trucks', 'Tractor', 'Bus', 'Gearbox'].includes(vehicleType) && (
                        <Grid item xs={12} sm={12} md={12}>
                           AdBlue ECU type
                           <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                              <Select
                                 value={adBlueClass}
                                 displayEmpty
                                 onChange={(e) => setAdBlueClass(e.target.value)}
                              >
                                 <MenuItem value="">
                                    <em>Select ECU type</em>
                                 </MenuItem>

                                 <MenuItem value="EDC17">
                                    Bosch EDC17
                                 </MenuItem>

                                 <MenuItem value="MD1_SID_9DF">
                                    MD1 / 9DF / SID209 / SID212 / SID321
                                 </MenuItem>

                              </Select>
                           </FormControl>
                        </Grid>
                     )}

                     {tuningType && (
                        <Grid item xs={12} sm={12} md={12}>
                           <Box
                              sx={{
                                 p: 2,
                                 border: '1px solid #dfe3e8',
                                 borderRadius: 1,
                                 backgroundColor: '#f8fafc',
                              }}
                           >
                              <Typography
                                 variant="subtitle2"
                                 sx={{ fontWeight: 700, mb: 1 }}
                              >
                                 Estimated price
                              </Typography>

                              {pricingLoading ? (
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       alignItems: 'center',
                                       gap: 1,
                                    }}
                                 >
                                    <CircularProgress size={18} />
                                    <Typography variant="body2">
                                       Calculating price...
                                    </Typography>
                                 </Box>
                              ) : pricingQuote?.autoPriced ? (
                                 <>
                                    <Box
                                       sx={{
                                          display:
                                             Number(pricingQuote.standardCredit) > 0
                                                ? 'flex'
                                                : 'none',
                                          justifyContent: 'space-between',
                                          gap: 2,
                                       }}
                                    >
                                       <Typography variant="body2">
                                          {pricingQuote.standard?.service}
                                       </Typography>
                                       <Typography variant="body2">
                                          {pricingQuote.standardCredit} credits
                                       </Typography>
                                    </Box>

                                    {(pricingQuote.specials || []).map((item) => (
                                       <Box
                                          key={item.priceId}
                                          sx={{
                                             display: 'flex',
                                             justifyContent: 'space-between',
                                             gap: 2,
                                             mt: 0.5,
                                          }}
                                       >
                                          <Typography variant="body2">
                                             {item.service}
                                          </Typography>
                                          <Typography variant="body2">
                                             +{item.credit} credits
                                          </Typography>
                                       </Box>
                                    ))}

                                    {pricingQuote.discountPercent > 0 && (
                                       <Box
                                          sx={{
                                             display: 'flex',
                                             justifyContent: 'space-between',
                                             gap: 2,
                                             mt: 0.5,
                                          }}
                                       >
                                          <Typography variant="body2">
                                             Account discount ({pricingQuote.discountPercent}%)
                                          </Typography>
                                          <Typography variant="body2">
                                             -{pricingQuote.discountCredit} credits
                                          </Typography>
                                       </Box>
                                    )}

                                    <Divider sx={{ my: 1 }} />

                                    <Box
                                       sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          gap: 2,
                                       }}
                                    >
                                       <Typography sx={{ fontWeight: 700 }}>
                                          Estimated total
                                       </Typography>
                                       <Typography sx={{ fontWeight: 700 }}>
                                          {pricingQuote.customerCredit} credits
                                       </Typography>
                                    </Box>
                                 </>
                              ) : pricingQuote?.manualReview ? (
                                 <>
                                    <Typography variant="body2">
                                       {pricingQuote.reason ||
                                          'This request requires manual pricing.'}
                                    </Typography>
                                    <Typography
                                       variant="caption"
                                       sx={{ display: 'block', mt: 0.5 }}
                                    >
                                       Final pricing will be confirmed during file processing.
                                    </Typography>
                                 </>
                              ) : pricingQuote?.valid === false ? (
                                 <Typography variant="body2">
                                    {pricingQuote.reason ||
                                       'Invalid service combination.'}
                                 </Typography>
                              ) : null}
                           </Box>
                        </Grid>
                     )}

                     <Grid item xs={12} sm={12} md={12}>
                        Message to our engineers
                        <Textarea
                           minRows={4}
                           size="sm"
                           placeholder="Do you have multiple options? Write them here, or just leave a message."
                           value={message}
                           onChange={(e) => setMessage(e.target.value)}
                        />
                     </Grid>
                     <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        className="term-check-box"
                     >
                        <FormControlJoy>
                           <RadioGroupJoy
                              defaultValue="false"
                              name="controlled-radio-buttons-group"
                              value={term}
                              onChange={(e) => setTerm(e.target.value)}
                              style={{ display: 'flex' }}
                           >
                              <p>
                                 <RadioJoy value="true" />
                                 &nbsp;I accept the{' '}
                                 <span
                                    style={{
                                       color: '#163A5F',
                                       cursor: 'pointer',
                                    }}
                                    onClick={() => setTermViewFlag(true)}
                                 >
                                    terms and conditions
                                 </span>
                              </p>
                           </RadioGroupJoy>
                        </FormControlJoy>
                     </Grid>
                     <Grid
                        item
                        xs={12}
                        sm={4}
                        md={4}
                        className="upload-btn-box"
                     >
                        <Button
                           variant="contained"
                           className="btn_blue"
                           style={{ padding: '10px 0' }}
                           fullWidth
                           disabled={
                              uploadBtnFlag ||
                              isLoading ||
                              (
                                 String(tuningType).trim().toLowerCase() === 'original' &&
                                 requestedServices.length === 0
                              )
                           }
                           onClick={upload}
                        >
                           {isLoading ? (
                              <BeatLoader color="#fff" size={10} />
                           ) : (
                              enoughCredits? 'Upload' : 'Not enough credits'
                           )}
                        </Button>
                     </Grid>
                  </Grid>
               </Box>
            </>
         )}
      </Box>
   )
}
